/**
 * TanStack Query hook for the Claude (Anthropic) usage panel.
 *
 * **This panel's expected state is `no_credential`, and that is not an error.**
 * The claude.ai Team plan has no metrics API — Team admins get the in-product
 * dashboard and CSV export only — so until a Claude Console organisation
 * exists and its Admin API key lands in `ANTHROPIC_ADMIN_KEY`, the backend
 * answers `access_state: "no_credential"` with copy naming exactly what
 * unlocks it, and makes no outbound request. The panel lights up with no code
 * change when the key is deployed.
 *
 * The route never fails because Anthropic is unconfigured, unauthorised or
 * unavailable: a missing key, a 401 and a 5xx are all *fields* on a 200
 * (`configured` / `access_state` / `status_detail` / `errors`). A thrown
 * error from this hook therefore means the **NOVA** API is down, not
 * Anthropic — same contract as `./ai.ts` and `./defender.ts`.
 *
 * **The poll interval comes from the payload, not from a constant here.** The
 * response carries `cache_ttl_sec` — how long the backend reuses a snapshot
 * before it re-reads the Admin API — and polling faster than that just
 * re-serves identical bytes. Same reasoning as every other hook file.
 *
 * **Nothing here is persisted.** `../persist.ts` is a deny-by-default
 * allow-list and `["claude", …]` is deliberately absent from it: the payload
 * carries what the org spends on Claude. (The Claude Code figures are org
 * totals with a k>=5 cohort floor applied at ingest — no email, name or
 * member id ever reaches this client.)
 *
 * SDK functions are imported from `@/lib/api/generated` rather than the
 * `@/lib/api` barrel, matching every other hook file: the barrel re-exports
 * this hook, so importing it here would close an import cycle.
 */

import { useQuery } from "@tanstack/react-query";

import { getClaudeOverview } from "@/lib/api/generated";
import { logger } from "@/lib/logger";

/** Query key for the Claude usage panel. */
export const CLAUDE_OVERVIEW_KEY = ["claude", "overview"] as const;

/**
 * Interval used only before the first response arrives, in ms.
 *
 * Five minutes — shorter than the backend's default 15-minute TTL, so the
 * first refetch can never be slower than the backend's own rebuild. After
 * that the payload's own `cache_ttl_sec` takes over.
 */
const FALLBACK_INTERVAL_MS = 300_000;

/** Refetch on the cadence the payload itself declares. */
function intervalFromTtl(data: { cache_ttl_sec: number } | undefined): number {
  const ttlSec = data?.cache_ttl_sec;
  return ttlSec != null && ttlSec > 0 ? ttlSec * 1000 : FALLBACK_INTERVAL_MS;
}

/**
 * The Claude usage panel: token usage, USD cost and Claude Code org totals
 * from the Anthropic Console Admin API — or, today, the fully-explained
 * unconfigured state.
 */
export function useClaudeOverview() {
  return useQuery({
    queryKey: CLAUDE_OVERVIEW_KEY,
    queryFn: async () => {
      const { data } = await getClaudeOverview();
      // Counts and states only — never spread a spend payload into a log line.
      logger.debug("[claude] overview received", {
        accessState: data?.access_state,
        configured: data?.configured,
        usageDays: data?.usage_days.length ?? 0,
        models: data?.usage_by_model.length ?? 0,
        truncated: data?.truncated,
        errors: data?.errors.length ?? 0,
      });
      return data;
    },
    refetchInterval: (query) => intervalFromTtl(query.state.data),
  });
}
